import React, { useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"
import { getLearnerApiErrorMessage } from "../../../auth/learner/errors"
import EmptyState from "../Questions/components/EmptyState"
import QuestionDetailPanel from "../Questions/components/QuestionDetailPanel"
import useQuestionDetail from "../Questions/hooks/useQuestionDetail"
import type { DetailTab } from "../Questions/types"
import BookmarksEmpty from "./components/BookmarksEmpty"
import BookmarksHeader from "./components/BookmarksHeader"
import BookmarksList from "./components/BookmarksList"
import useBookmarks from "./hooks/useBookmarks"
import "./BookmarksPage.style.scss"

const BookmarksPage = () => {
  const [params, setParams] = useSearchParams()
  const detail = useQuestionDetail()
  const detailId = params.get("id")
  const detailOpen = Boolean(detailId)
  const openTabRef = useRef<DetailTab>("attempts")
  const {
    cards,
    pagination,
    loading,
    hasLoaded,
    error,
    setPage,
    removeBookmark,
    dropLocal,
    removingId,
  } = useBookmarks()

  const savedCount = pagination?.total ?? cards.length
  const showEmpty = hasLoaded && !error && cards.length === 0 && !detailOpen

  useEffect(() => {
    if (!detailId) {
      detail.close()
      return
    }
    detail.open(detailId, openTabRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId])

  const openDetail = (id: string, tab: DetailTab = "attempts") => {
    openTabRef.current = tab
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", id)
      return next
    })
  }

  const closeDetail = () => {
    detail.close()
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete("id")
      return next
    })
  }

  const handleRemove = async (questionId: string) => {
    const removed = await removeBookmark(questionId)
    if (removed && detailId === questionId) closeDetail()
  }

  const handleDetailBookmark = async () => {
    const id = detail.detail?.id
    if (!id) return
    try {
      const next = await detail.toggleBookmark()
      toast.success(next ? "Bookmarked" : "Bookmark removed")
      if (!next) {
        await dropLocal(id)
        closeDetail()
      }
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not update bookmark"))
    }
  }

  return (
    <div className={`bookmarks-page${detailOpen ? " is-detail-open" : ""}`}>
      <div className="bookmarks-page__list">
        <div className="bookmarks-page__inner">
          <BookmarksHeader savedCount={savedCount} />

          {!hasLoaded && loading && (
            <EmptyState title="Loading bookmarks…" />
          )}

          {hasLoaded && error && (
            <EmptyState title="Couldn’t load bookmarks" description={error} />
          )}

          {showEmpty && <BookmarksEmpty />}

          {cards.length > 0 && (
            <BookmarksList
              cards={cards}
              pagination={pagination}
              loading={loading}
              removingId={removingId}
              selectedId={detailId}
              onPageChange={setPage}
              onOpen={openDetail}
              onRemove={(questionId) => {
                void handleRemove(questionId)
              }}
            />
          )}
        </div>
      </div>

      {detailOpen ? (
        <QuestionDetailPanel
          detail={detail.detail}
          loading={detail.loading}
          error={detail.error}
          activeTab={detail.activeTab}
          savingNote={detail.savingNote}
          savingTag={detail.savingTag}
          onTabChange={detail.setActiveTab}
          onClose={closeDetail}
          onToggleBookmark={handleDetailBookmark}
          onSaveNote={detail.saveNote}
          onAddTag={detail.addTag}
          onRemoveTag={detail.removeTag}
        />
      ) : null}
    </div>
  )
}

export default BookmarksPage
